/**
 * Haze estimation from a single RGB frame — dark-channel prior.
 *
 * WHY THIS EXISTS: every flood or traffic signal in the wall comes from either a
 * sensor or someone else's model. Nobody was measuring the *air* over the campus,
 * and haze quietly corrupts every other visual reading: a grey wash lowers
 * contrast, so a wet road reads dry, a dark street reads flooded, and a frame
 * that looks fine to a human is often the one a vision model gets wrong.
 *
 * This is real computer vision, not a heuristic dressed up as one. It is the
 * dark-channel prior of He, Sun & Tang, "Single Image Haze Removal Using Dark
 * Channel Prior" (CVPR 2009) — the paper behind most dehazing code in
 * existence, and the one that won the 2009 Marr Prize. Given a hazy outdoor
 * image the atmospheric light A is well approximated by the brightest pixels
 * of the dark channel, because in local patches of a *hazy* image some channel
 * is always driven near zero by the veil. That gives a per-pixel transmission
 * estimate t(x) = 1 - omega * minChannel(x)/A, and haze density follows.
 *
 * No model download, no GPU, no network: ~O(pixels) with a separable min-filter.
 * That is deliberate. This runs on every frame of every tile in the wall, in the
 * browser, on the exact pixels the operator is looking at.
 *
 * HONESTY: this is a *scene-level haze estimate from imagery*, not a PM2.5
 * reading. Correlation with AQI is real but imperfect — a humid clear day can
 * read hazier than a polluted crisp one, and one camera cannot see the whole
 * city. The panel labels it as an image estimate and pairs it with the
 * regulatory Air4Thai AQI feed so the two can be compared rather than conflated.
 */

/** Working resolution. Haze is a low-frequency property; 160px wide is plenty
 *  and keeps every tile cheap enough to re-run on a timer. */
export const HAZE_WORK_WIDTH = 160;

export interface HazeResult {
  /**
   * Atmospheric optical depth, -ln(mean transmission). THE headline number,
   * because it is the quantity He et al. (2009) actually estimate — a unitless
   * extinction length, where ~0.1 is thin air and 1.0 means the scene is behind
   * one e-fold of veil. It is deliberately NOT wrapped in a 0-100 "haze index"
   * with clear/light/moderate/heavy bands: those thresholds appear in no source
   * and a band label reads as a published grade. See hazeSeverity() for the
   * honest replacement.
   */
  aod: number;
  /** Mean estimated transmission, 0–1. */
  transmission: number;
  /** Share of this frame's pixels with transmission < 0.6, 0–1. A count of
   *  this frame's pixels, not a position on a calibrated scale. */
  veiledFraction: number;
  /** Share of local contrast surviving the veil, 0–1. Independent cross-check. */
  contrastRetained: number;
  /** Atmospheric light, normalised RGB — the colour of the veil itself. */
  atmosphericLight: [number, number, number];
  /** Fraction of pixels whose dark channel is near zero — the veil signature. */
  darkChannelFraction: number;
}

export interface Rgb {
  data: Uint8ClampedArray;
  width: number;
  height: number;
}

/**
 * Box-minimum over a sliding window, separable: horizontal pass then vertical.
 * A naive 15×15 neighbourhood costs 225 ops/pixel; this costs 2×15.
 * Returns Float32Array of per-pixel minima of `channel`.
 */
function windowMin(src: Float32Array, w: number, h: number, r: number): Float32Array {
  const tmp = new Float32Array(w * h);
  const out = new Float32Array(w * h);

  // Horizontal pass.
  for (let y = 0; y < h; y++) {
    const row = y * w;
    for (let x = 0; x < w; x++) {
      let m = Infinity;
      const x0 = Math.max(0, x - r);
      const x1 = Math.min(w - 1, x + r);
      for (let i = x0; i <= x1; i++) {
        const v = src[row + i];
        if (v < m) m = v;
      }
      tmp[row + x] = m;
    }
  }
  // Vertical pass.
  for (let x = 0; x < w; x++) {
    for (let y = 0; y < h; y++) {
      let m = Infinity;
      const y0 = Math.max(0, y - r);
      const y1 = Math.min(h - 1, y + r);
      for (let i = y0; i <= y1; i++) {
        const v = tmp[i * w + x];
        if (v < m) m = v;
      }
      out[y * w + x] = m;
    }
  }
  return out;
}

/** Box-average over the same window shape, for the contrast metric. */
function windowMean(src: Float32Array, w: number, h: number, r: number): Float32Array {
  const tmp = new Float32Array(w * h);
  const out = new Float32Array(w * h);
  const inv = 1 / ((2 * r + 1) * (2 * r + 1));
  for (let y = 0; y < h; y++) {
    const row = y * w;
    for (let x = 0; x < w; x++) {
      let s = 0;
      const x0 = Math.max(0, x - r);
      const x1 = Math.min(w - 1, x + r);
      for (let i = x0; i <= x1; i++) s += src[row + i];
      tmp[row + x] = s / (x1 - x0 + 1);
    }
  }
  for (let x = 0; x < w; x++) {
    for (let y = 0; y < h; y++) {
      let s = 0;
      const y0 = Math.max(0, y - r);
      const y1 = Math.min(h - 1, y + r);
      for (let i = y0; i <= y1; i++) s += tmp[i * w + x];
      out[y * w + x] = s / (y1 - y0 + 1);
    }
  }
  void inv;
  return out;
}

/** Nearest-neighbour box downscale of an RGBA buffer. */
export function downscaleRgba(
  data: Uint8ClampedArray,
  srcW: number,
  srcH: number,
  dstW: number,
): Rgb {
  const ratio = srcW / dstW;
  const dstH = Math.max(1, Math.round(srcH / ratio));
  const out = new Uint8ClampedArray(dstW * dstH * 4);
  for (let y = 0; y < dstH; y++) {
    const sy = Math.min(srcH - 1, Math.floor(y * ratio));
    for (let x = 0; x < dstW; x++) {
      const sx = Math.min(srcW - 1, Math.floor(x * ratio));
      const si = (sy * srcW + sx) * 4;
      const di = (y * dstW + x) * 4;
      out[di] = data[si];
      out[di + 1] = data[si + 1];
      out[di + 2] = data[si + 2];
      out[di + 3] = 255;
    }
  }
  return { data: out, width: dstW, height: dstH };
}

/** Pull an ImageData-like RGBA buffer out of an HTMLImageElement via canvas. */
export function imageToRgba(img: HTMLImageElement): Rgb | null {
  const w = img.naturalWidth || img.width;
  const h = img.naturalHeight || img.height;
  if (!w || !h) return null;
  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d", { willReadFrequently: true });
  if (!ctx) return null;
  ctx.drawImage(img, 0, 0);
  try {
    const id = ctx.getImageData(0, 0, w, h);
    return { data: id.data, width: w, height: h };
  } catch {
    // Tainted canvas (cross-origin without CORS). Callers treat this as
    // "unavailable", never as "clear" — see honesty note below.
    return null;
  }
}

/**
 * Is this frame a picture of something, at all?
 *
 * Found by running the detector against a real camera instead of a fixture. The
 * nearest iTIC camera to the campus was serving the vendor's "No signal"
 * placeholder — a near-black card with white text — and the haze estimator
 * happily returned "2, clear". That is the single worst output this system could
 * produce: a dead camera rendered as a reassuring all-clear. An operator would
 * read it as a measured, healthy atmosphere on a road nobody is watching.
 *
 * Structure alone does not catch it, because the white text supplies plenty of
 * luminance spread. What distinguishes a dead feed is that almost the whole
 * frame is at the bottom of the range. A real night street is dark but is not
 * *empty*: it has headlights, signage, lit windows, wet reflections.
 *
 * So the wall asks this question before it asks anything else, and the answer
 * "no signal" is surfaced on the tile rather than being smoothed into a reading.
 */
export interface FrameHealth {
  usable: boolean;
  /** Why it cannot be judged. Omitted when usable. */
  reason?: "no-signal" | "no-structure";
  /** Share of pixels below 0.08 luma. */
  nearBlackFraction: number;
  /** Luminance standard deviation. */
  lumStd: number;
}

/** Above this share of near-black pixels, the feed is dark rather than dim. */
const NEAR_BLACK_LIMIT = 0.6;
const NEAR_BLACK_LEVEL = 0.08;

export function assessFrame(rgb: Rgb): FrameHealth {
  const { data, width: w, height: h } = rgb;
  const n = w * h;
  if (n === 0 || w < 8 || h < 8) {
    return { usable: false, reason: "no-signal", nearBlackFraction: 1, lumStd: 0 };
  }
  let sum = 0;
  let nearBlack = 0;
  for (let i = 0; i < n; i++) {
    const l =
      (0.2126 * data[i * 4] + 0.7152 * data[i * 4 + 1] + 0.0722 * data[i * 4 + 2]) / 255;
    sum += l;
    if (l < NEAR_BLACK_LEVEL) nearBlack++;
  }
  const mean = sum / n;
  let v = 0;
  for (let i = 0; i < n; i++) {
    const l =
      (0.2126 * data[i * 4] + 0.7152 * data[i * 4 + 1] + 0.0722 * data[i * 4 + 2]) / 255;
    v += (l - mean) ** 2;
  }
  const lumStd = Math.sqrt(v / n);
  const nearBlackFraction = nearBlack / n;

  if (nearBlackFraction > NEAR_BLACK_LIMIT) {
    return { usable: false, reason: "no-signal", nearBlackFraction, lumStd };
  }
  if (lumStd < 0.02) {
    return { usable: false, reason: "no-structure", nearBlackFraction, lumStd };
  }
  return { usable: true, nearBlackFraction, lumStd };
}

const RADIUS = 3; // 7×7 at 160px wide ≈ the paper's 15×15 at native res
const OMEGA = 0.95; // standard DCP weight

/**
 * Dark-channel prior haze estimate.
 * Returns null when the frame is unusable (too small, or untainted pixels).
 */
export function estimateHaze(rgb: Rgb): HazeResult | null {
  const { data, width: w, height: h } = rgb;
  const n = w * h;
  if (n === 0 || w < 8 || h < 8) return null;

  // Gate on frame health first. A dead feed is not a clear day, and printing a
  // number for one is the failure that turns a blind spot into a false all-clear.
  const health = assessFrame(rgb);
  if (!health.usable) return null;

  const r = new Float32Array(n);
  const g = new Float32Array(n);
  const b = new Float32Array(n);
  const lum = new Float32Array(n);
  for (let i = 0; i < n; i++) {
    const rr = data[i * 4] / 255;
    const gg = data[i * 4 + 1] / 255;
    const bb = data[i * 4 + 2] / 255;
    r[i] = rr;
    g[i] = gg;
    b[i] = bb;
    lum[i] = 0.2126 * rr + 0.7152 * gg + 0.0722 * bb;
  }

  // Dark channel: per-pixel min across channels, then window min across space.
  const minAcrossChannels = new Float32Array(n);
  for (let i = 0; i < n; i++) {
    const m = r[i] < g[i] ? r[i] : g[i];
    minAcrossChannels[i] = m < b[i] ? m : b[i];
  }
  const dark = windowMin(minAcrossChannels, w, h, RADIUS);

  // REFUSAL GUARD — structure test on the image itself, not on the dark channel.
  //
  // The prior infers a veil by comparing near-zero regions (shadow, foliage) with
  // bright ones. If the frame has no such regions — a blank card, a blown-out
  // white field, a lens cap — every pixel looks equally veiled and the formula
  // reports heavy haze on a frame with no scene in it at all.
  //
  // Measured across the fixtures (clear → 80% veil → blank):
  //
  //   clear street   lumStd 0.252
  //   veil 0.45      lumStd 0.139
  //   veil 0.80      lumStd 0.050
  //   blank field    lumStd 0.000   ← no scene, therefore no atmosphere
  //
  // Luminance spread separates them cleanly where a dark-channel threshold could
  // not: a heavy veil compresses the dark channel just as much as a blank field
  // does, so any level test tuned to catch the blank also throws away the
  // thickest real haze readings. Spread does not, because veiling can only
  // compress structure, never erase it.
  //
  // A confidently wrong number is worse than no number — an operator cannot tell
  // it from a real reading — so refuse and let the panel say "unavailable".
  let lumSum = 0;
  for (let i = 0; i < n; i++) lumSum += lum[i];
  const lumMean = lumSum / n;
  let lumVar = 0;
  for (let i = 0; i < n; i++) lumVar += (lum[i] - lumMean) ** 2;
  const lumStd = Math.sqrt(lumVar / n);
  if (lumStd < 0.02) return null;

  // Atmospheric light: brightest 0.1% of dark-channel pixels, by luminance in
  // the image. This is the one genuinely assumption-laden step — it assumes
  // the scene contains some sky-ish or bright region. On a frame that is almost
  // entirely close-up geometry the estimate degrades gracefully toward the
  // brightest thing present, which biases the haze reading DOWN (says clearer
  // than it is), never up. Erring toward "clear" is the safer failure for an
  // operator deciding whether to trust a frame.
  const topN = Math.max(1, Math.floor(n * 0.001));
  const idx = Array.from({ length: n }, (_, i) => i).sort(
    (p, q) => dark[q] - dark[p] || lum[q] - lum[p],
  );
  let ar = 0;
  let ag = 0;
  let ab = 0;
  for (let k = 0; k < topN; k++) {
    const i = idx[k];
    ar += r[i];
    ag += g[i];
    ab += b[i];
  }
  const A: [number, number, number] = [ar / topN, ag / topN, ab / topN];
  // Guard: a near-black A makes the ratio explode and reports maximum haze.
  const aMean = (A[0] + A[1] + A[2]) / 3;
  if (!(aMean > 0.02)) {
    return {
      aod: 0,
      transmission: 1,
      veiledFraction: 0,
      contrastRetained: 1,
      atmosphericLight: A,
      darkChannelFraction: 0,
    };
  }

  let sumT = 0;
  let darkFrac = 0;
  const t = new Float32Array(n);
  for (let i = 0; i < n; i++) {
    const ratio = dark[i] / aMean;
    let ti = 1 - OMEGA * ratio;
    if (ti < 0.05) ti = 0.05; // floor keeps AOD finite
    t[i] = ti;
    sumT += ti;
    if (ratio > 0.9) darkFrac++;
  }
  const meanT = sumT / n;
  const aod = -Math.log(Math.max(1e-3, meanT));

  // Contrast retention: how much of the scene's local variation survives the
  // veil. Kept as an independent cross-check on the same maths — a genuinely
  // hazy frame has lost local contrast even where the dark-channel estimate is
  // uncertain — but deliberately NOT blended into the headline number. The
  // blend used to be an invented 0-100 index with clear/light/moderate/heavy
  // bands at thresholds 18/38/62 that appear nowhere in He et al. The paper
  // yields AOD. A scale nobody chose, wearing the authority of one that was
  // chosen, is the same failure as inventing a second water-level figure to sit
  // beside the one that has a source.
  const localContrast = windowMean(lum, w, h, RADIUS);
  let cc = 0;
  for (let i = 0; i < n; i++) cc += Math.abs(lum[i] - localContrast[i]);
  const contrastRetained = 1 - Math.min(1, (cc / n) * 6);

  // "Veiled fraction": share of pixels whose transmission is low enough that the
  // veil is doing real work on them. This one is safe to show as a percentage
  // because the percentage means exactly what it says — a count of this frame's
  // pixels — rather than a position on a scale that would need a calibration
  // nobody performed.
  let veiled = 0;
  for (let i = 0; i < n; i++) if (t[i] < 0.6) veiled++;

  return {
    aod: Math.round(aod * 1000) / 1000,
    transmission: Math.round(meanT * 1000) / 1000,
    veiledFraction: Math.round((veiled / n) * 1000) / 1000,
    contrastRetained: Math.round(contrastRetained * 1000) / 1000,
    atmosphericLight: [
      Math.round(A[0] * 100) / 100,
      Math.round(A[1] * 100) / 100,
      Math.round(A[2] * 100) / 100,
    ],
    darkChannelFraction: Math.round((darkFrac / n) * 1000) / 1000,
  };
}

/** Working-width downscale then estimate — what the wall actually calls. */
export function estimateHazeFromImageData(
  data: Uint8ClampedArray,
  width: number,
  height: number,
): HazeResult | null {
  const scaled = downscaleRgba(data, width, height, HAZE_WORK_WIDTH);
  return estimateHaze(scaled);
}

/**
 * Severity for the tile's meter colour ONLY.
 *
 * The thresholds below are a LOCAL CONVENTION, stated here as such and not
 * dressed as a published grade. AOD is a physical quantity with no agreed
 * clear/hazy boundary for street-level imagery — the literature's boundaries
 * are for sun photometers at fixed sites, which is a different measurement.
 * So the number shown is AOD, and this function exists purely so the bar has a
 * colour; the label it used to return ("clear", "moderate haze") is gone,
 * because a band name is a claim about air quality that this image cannot make.
 */
export function hazeSeverity(aod: number): "clear" | "light" | "moderate" | "heavy" {
  if (aod < 0.2) return "clear";
  if (aod < 0.5) return "light";
  if (aod < 1.0) return "moderate";
  return "heavy";
}
